import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as api from '../api/client';

export function useIncidents(params?: { limit?: number; days?: number; service_id?: number }) {
  return useQuery({
    queryKey: ['incidents', params],
    queryFn: () => api.getIncidents(params),
    refetchInterval: 60_000,
  });
}

export function useIncidentSummary(days = 30) {
  return useQuery({
    queryKey: ['incident-summary', days],
    queryFn: () => api.getIncidentSummary(days),
    refetchInterval: 60_000,
  });
}

/** Both mutations refresh the list and the summary, since MTTR shifts too. */
function useIncidentMutation<TArgs>(fn: (args: TArgs) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['incidents'] });
      qc.invalidateQueries({ queryKey: ['incident-summary'] });
    },
  });
}

export function useResolveIncident() {
  return useIncidentMutation((id: number) => api.resolveIncident(id));
}

export function useAcknowledgeIncident() {
  return useIncidentMutation(({ id, note }: { id: number; note?: string }) =>
    api.acknowledgeIncident(id, note));
}

export function useSetIncidentPublic() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: { public?: boolean; public_title?: string | null } }) => api.setIncidentPublic(id, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['incidents'] }); },
  });
}

export function useIncidentUpdates(id: number, enabled = true) {
  return useQuery({ queryKey: ['incident-updates', id], queryFn: () => api.getIncidentUpdates(id), enabled: id > 0 && enabled });
}

export function usePostIncidentUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: { status?: string | null; title: string; body?: string } }) => api.postIncidentUpdate(id, data),
    onSuccess: (_, v) => { qc.invalidateQueries({ queryKey: ['incident-updates', v.id] }); },
  });
}

export function useDeleteIncidentUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, postId }: { id: number; postId: number }) => api.deleteIncidentUpdate(id, postId),
    onSuccess: (_, v) => { qc.invalidateQueries({ queryKey: ['incident-updates', v.id] }); },
  });
}
